import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi.js';
import { useAuth } from '../context/AuthContext.jsx';
import { Lock, Unlock } from 'lucide-react';

const FALLBACK_ROLES = ['kitchen', 'front_of_house', 'cashier', 'manager', 'bar', 'delivery'];

/**
 * ShiftModal – Add or edit a shift.
 *
 * Props:
 *   shift: object | null  (null = add mode)
 *   date: string          YYYY-MM-DD (pre-fill for add mode)
 *   role: string          pre-fill role for add mode
 *   employees: array      list of employees
 *   rosterId: string      roster UUID
 *   onClose(): void
 *   onSaved(shift): void
 *   onDeleted(shiftId): void
 */
export default function ShiftModal({ shift, date, role, employees, rosterId, onClose, onSaved, onDeleted, roleNames }) {
  const ROLES = roleNames?.length > 0 ? roleNames : FALLBACK_ROLES;
  const api = useApi();
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const isEditing = !!shift;

  const [form, setForm] = useState({
    employee_id: shift?.employee_id || '',
    date: shift?.date || date || '',
    start_time: shift?.start_time?.slice(0, 5) || '',
    end_time: shift?.end_time?.slice(0, 5) || '',
    role: shift?.role || role || 'kitchen',
    notes: shift?.notes || '',
  });

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openingShift, setOpeningShift] = useState(false);
  const [lockingShift, setLockingShift] = useState(false);
  const [isLocked, setIsLocked] = useState(!!shift?.is_locked);
  const [error, setError] = useState('');
  const [lockAcquired, setLockAcquired] = useState(false);

  // Acquire edit lock when editing
  useEffect(() => {
    if (isEditing && isOwner && shift?.id) {
      api.post(`/shifts/${shift.id}/edit-lock`).then(() => setLockAcquired(true)).catch(() => {});
      return () => {
        // Release lock on unmount
        api.delete(`/shifts/${shift.id}/edit-lock`).catch(() => {});
      };
    }
  }, []);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError('');
  }

  async function handleSave(e) {
    e.preventDefault();
    setError('');

    if (!form.date || !form.start_time || !form.end_time || !form.role) {
      setError('Date, start time, end time, and role are required.');
      return;
    }
    if (form.start_time >= form.end_time) {
      setError('Start time must be before end time.');
      return;
    }

    setSaving(true);
    try {
      let saved;
      if (isEditing) {
        saved = await api.put(`/shifts/${shift.id}`, form);
      } else {
        saved = await api.post('/shifts', { ...form, roster_id: rosterId });
      }
      onSaved(saved);
    } catch (err) {
      setError(err.message || 'Failed to save shift.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm('Remove this shift?')) return;
    setDeleting(true);
    try {
      await api.delete(`/shifts/${shift.id}`);
      onDeleted(shift.id);
    } catch (err) {
      setError(err.message || 'Failed to delete shift.');
    } finally {
      setDeleting(false);
    }
  }

  async function handleMarkOpen() {
    if (!window.confirm('Mark this shift as open (employee is unavailable)?')) return;
    setOpeningShift(true);
    try {
      const updated = await api.post(`/shifts/${shift.id}/open`);
      onSaved(updated);
    } catch (err) {
      setError(err.message || 'Failed to mark shift as open.');
    } finally {
      setOpeningShift(false);
    }
  }

  async function handleToggleLock() {
    const action = isLocked ? 'unlock' : 'lock';
    setLockingShift(true);
    try {
      const updated = await api.post(`/shifts/${shift.id}/${action}`);
      setIsLocked(updated.is_locked);
      onSaved(updated);
    } catch (err) {
      setError(err.message || `Failed to ${action} shift.`);
    } finally {
      setLockingShift(false);
    }
  }

  const activeEmployees = employees?.filter(e => e.is_active) || [];

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isEditing ? 'Edit Shift' : 'Add Shift'}
            {isEditing && isLocked && (
              <span style={{ fontSize: 12, fontWeight: 600, background: '#e0f2fe', color: '#0369a1', border: '1px solid #7dd3fc', borderRadius: 4, padding: '1px 7px' }}>
                Locked
              </span>
            )}
          </span>
          <button className="modal-close" onClick={onClose}>&#x2715;</button>
        </div>

        <form onSubmit={handleSave}>
          <div className="modal-body">
            {error && <div className="alert alert-error">{error}</div>}

            {isEditing && shift.editing_by && !lockAcquired && (
              <div className="alert alert-warning">This shift is being edited by another user.</div>
            )}

            <div className="form-group">
              <label>Employee</label>
              <select className="form-control" name="employee_id" value={form.employee_id} onChange={handleChange}>
                <option value="">Unassigned</option>
                {activeEmployees.map(e => (
                  <option key={e.id} value={e.id}>{e.name} ({e.role})</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Role <span className="required">*</span></label>
              <select className="form-control" name="role" value={form.role} onChange={handleChange} required>
                {ROLES.map(r => (
                  <option key={r} value={r}>{r.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Date <span className="required">*</span></label>
              <input
                type="date"
                className="form-control"
                name="date"
                value={form.date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Time <span className="required">*</span></label>
                <input
                  type="time"
                  className="form-control"
                  name="start_time"
                  value={form.start_time}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>End Time <span className="required">*</span></label>
                <input
                  type="time"
                  className="form-control"
                  name="end_time"
                  value={form.end_time}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Notes</label>
              <input
                type="text"
                className="form-control"
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Optional notes..."
              />
            </div>
          </div>

          <div className="modal-footer">
            {isEditing && isOwner && !shift.is_open && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleToggleLock}
                disabled={lockingShift}
                style={{ marginRight: 'auto' }}
              >
                {lockingShift ? '...' : isLocked
                  ? <><Unlock size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />Unlock Shift</>
                  : <><Lock size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />Lock Shift</>
                }
              </button>
            )}
            {isEditing && isOwner && !shift.is_open && !isLocked && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleMarkOpen}
                disabled={openingShift}
              >
                {openingShift ? 'Marking...' : 'Mark as Open'}
              </button>
            )}
            {isEditing && isOwner && (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            {isOwner && (
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <span className="spinner" /> : (isEditing ? 'Save Changes' : 'Add Shift')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
